/**
 * useSubscription Hook - Credit System with Multiple Tiers
 */

import { useState, useEffect, useCallback } from 'react';
import subscriptionService, { SUBSCRIPTION_PLANS, CREDIT_COSTS } from '../services/subscriptionService';

export function useSubscription(userId) {
  const [isPremium, setIsPremium] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [credits, setCredits] = useState(5);
  const [subscriptionType, setSubscriptionType] = useState('free');
  const [monthlyCredits, setMonthlyCredits] = useState(5);
  const [creditResetDate, setCreditResetDate] = useState(null);

  /**
   * Initialize and check subscription status
   */
  const initialize = useCallback(async () => {
    if (!userId) {
      setIsLoading(false);
      setIsPremium(false);
      setCredits(5);
      setSubscriptionType('free');
      return;
    }

    try {
      setIsLoading(true);
      setError(null);

      await subscriptionService.initialize(userId);
      await refreshSubscriptionStatus();

    } catch (err) {
      setError(err.message || 'Failed to initialize');
      console.error('[useSubscription] Error:', err);
      setIsPremium(false);
    } finally {
      setIsLoading(false);
    }
  }, [userId]);

  /**
   * Refresh subscription and credit status from backend
   */
  const refreshSubscriptionStatus = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const status = await subscriptionService.checkPremiumStatus(token);
      
      setIsPremium(status.premium || status.subscription_type !== 'free');
      setCredits(status.credits ?? 5);
      setSubscriptionType(status.subscription_type || 'free');
      setMonthlyCredits(status.monthly_credits ?? 5);
      setCreditResetDate(status.credit_reset_date);
      
      // Update local storage
      const userStr = localStorage.getItem('user');
      if (userStr) {
        const user = JSON.parse(userStr);
        user.premium = status.premium || status.subscription_type !== 'free';
        user.credits = status.credits;
        user.subscription_type = status.subscription_type;
        user.monthly_credits = status.monthly_credits;
        localStorage.setItem('user', JSON.stringify(user));
      }
    } catch (err) {
      console.error('[useSubscription] Failed to refresh status:', err);
    }
  }, []);

  /**
   * Purchase subscription
   * @param {string} productId - SKU of the subscription
   */
  const purchaseSubscription = useCallback(async (productId) => {
    try {
      setIsLoading(true);
      setError(null);

      const result = await subscriptionService.purchaseSubscription(productId);
      
      // Update local state
      setIsPremium(result.premium);
      setSubscriptionType(result.subscription_type);
      setCredits(result.credits);
      setMonthlyCredits(result.monthly_credits);

      return result;
    } catch (err) {
      const errorMessage = err.message || 'Purchase failed';
      setError(errorMessage);
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, []);

  /**
   * Restore purchases
   */
  const restorePurchases = useCallback(async () => {
    try {
      setIsLoading(true);
      await subscriptionService.restorePurchases();
      await refreshSubscriptionStatus();
    } catch (err) {
      console.error('[useSubscription] Restore failed:', err);
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, [refreshSubscriptionStatus]);

  /**
   * Check if user can use a feature
   */
  const canUseFeature = useCallback((feature) => {
    return subscriptionService.canUseFeature(
      { credits, subscription_type: subscriptionType },
      feature
    );
  }, [credits, subscriptionType]);

  /**
   * Get credit cost for a feature
   */
  const getCreditCost = useCallback((feature) => {
    return CREDIT_COSTS[feature] || 1;
  }, []);

  /**
   * Get all subscription plans
   */
  const getPlans = useCallback((language = 'tr') => {
    return subscriptionService.getPlans(language);
  }, []);

  /**
   * Initialize on mount
   */
  useEffect(() => {
    initialize();
  }, [initialize]);

  return {
    // Status
    isPremium,
    isLoading,
    error,
    credits,
    subscriptionType,
    monthlyCredits,
    creditResetDate,
    isUnlimited: subscriptionType === 'elite',
    
    // Actions
    purchaseSubscription,
    restorePurchases,
    refreshSubscriptionStatus,
    
    // Helpers
    canUseFeature,
    getCreditCost,
    getPlans,
    
    // Constants
    SUBSCRIPTION_PLANS,
    CREDIT_COSTS
  };
}

export default useSubscription;
